/**
 * chart-math.ts — pure helpers behind the Audience line chart (no chart library; the admin
 * app hand-rolls its charts — D-07-06). Unit-tested in tests/unit/audience-chart-math.test.ts.
 */

export interface XY {
  x: number;
  y: number;
}

/** Smallest "nice" step (1, 2 or 5 × 10^k) that is ≥ rawStep. */
export function niceStep(rawStep: number): number {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
  const exp = Math.floor(Math.log10(rawStep));
  const base = Math.pow(10, exp);
  const frac = rawStep / base;
  const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return nice * base;
}

/**
 * A padded, tick-aligned y domain for a line of totals. Not forced to zero (follower
 * counts move by a fraction of their size — a zero baseline would flatten the story),
 * but never below zero when the data is non-negative.
 */
export function niceDomain(values: number[], tickCount = 5): { min: number; max: number; ticks: number[] } {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return { min: 0, max: 1, ticks: [0, 1] };

  let lo = Math.min(...finite);
  let hi = Math.max(...finite);
  if (lo === hi) {
    const pad = Math.max(1, Math.abs(lo) * 0.02);
    lo -= pad;
    hi += pad;
  } else {
    const pad = (hi - lo) * 0.08;
    lo -= pad;
    hi += pad;
  }
  if (Math.min(...finite) >= 0 && lo < 0) lo = 0;

  const step = niceStep((hi - lo) / Math.max(1, tickCount - 1));
  const min = Math.floor(lo / step) * step;
  const max = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let v = min; v <= max + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return { min, max, ticks };
}

/** Thousands-separated integer (en-US: 1,284). */
export function formatInt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

/** Axis-friendly compact number: 1,284 · 12.9K · 1.2M. */
export function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs < 10_000) return formatInt(n);
  if (abs < 1_000_000) return `${trimZero((n / 1_000).toFixed(1))}K`;
  return `${trimZero((n / 1_000_000).toFixed(1))}M`;
}

function trimZero(s: string): string {
  return s.replace(/\.0$/, '');
}

/** Signed delta with a thousands separator: +1,284 / −12 / ±0. */
export function formatSigned(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '±0';
  return `${n > 0 ? '+' : '−'}${formatInt(Math.abs(n))}`;
}

/** Linear scale factory. Degenerate domains map everything to the range midpoint. */
export function scaleLinear(d0: number, d1: number, r0: number, r1: number): (v: number) => number {
  if (d1 === d0) return () => (r0 + r1) / 2;
  const k = (r1 - r0) / (d1 - d0);
  return (v: number) => r0 + (v - d0) * k;
}

const fmt = (n: number): string => (Math.round(n * 100) / 100).toString();

/** SVG path for a polyline through the points ('' for < 2 points). */
export function linePath(pts: XY[]): string {
  if (pts.length < 2) return '';
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${fmt(p.x)} ${fmt(p.y)}`).join(' ');
}

/** SVG path for the area under the line down to `baseY` ('' for < 2 points). */
export function areaPath(pts: XY[], baseY: number): string {
  if (pts.length < 2) return '';
  const first = pts[0]!;
  const last = pts[pts.length - 1]!;
  return `${linePath(pts)} L${fmt(last.x)} ${fmt(baseY)} L${fmt(first.x)} ${fmt(baseY)} Z`;
}

/** Index of the x position closest to `x` (xs ascending). −1 for an empty list. */
export function nearestIndex(xs: number[], x: number): number {
  if (xs.length === 0) return -1;
  let best = 0;
  let bestDist = Math.abs(xs[0]! - x);
  for (let i = 1; i < xs.length; i++) {
    const d = Math.abs(xs[i]! - x);
    if (d < bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return best;
}

/**
 * Which point indices get an x-axis label: always the first and last, and evenly spaced
 * ones in between so that at most `maxLabels` are drawn.
 */
export function pickLabelIndices(count: number, maxLabels: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const n = Math.max(2, Math.min(maxLabels, count));
  const out = new Set<number>();
  for (let i = 0; i < n; i++) out.add(Math.round((i * (count - 1)) / (n - 1)));
  return [...out].sort((a, b) => a - b);
}

/** '2026-09-29' → '29 Sep' (UTC, no locale surprises). */
export function formatDayShort(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

/** '2026-09-29' → '29 Sep 2026'. */
export function formatDayLong(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return `${formatDayShort(day)} ${d.getUTCFullYear()}`;
}

export interface WindowSummary {
  first: number;
  last: number;
  delta: number;
  /** Percent change vs. the first value, null when the first value is 0. */
  pct: number | null;
}

/** First → last change over a series of values (null for an empty series). */
export function summarize(values: number[]): WindowSummary | null {
  if (values.length === 0) return null;
  const first = values[0]!;
  const last = values[values.length - 1]!;
  const delta = last - first;
  return { first, last, delta, pct: first === 0 ? null : (delta / first) * 100 };
}

/** 'YYYY-MM-DD' of the UTC calendar day. */
export function utcDay(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Shift a 'YYYY-MM-DD' by whole days (UTC). */
export function addDays(day: string, delta: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}
